import {useState, useEffect} from 'react';
import './SearchResults.css';
import BookmarksStore from "../../xstores/bookmarks";
import useObservable from "../../hooks/useObservable";
import Navigation from '../../components/Navigation/Navigation';
import Dropdown from '../../components/Dropdown/Dropdown';
import * as Styled from './SearchResultsStyles';
import {Container, Row, Col} from 'react-bootstrap';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import ErrorModal from '../../components/ErrorModal/ErrorModal';
import SearchStore from '../../xstores/search';
import ResultDetails from './ResultDetails';
import Filters from '../../components/Filter/Filters';
import SearchLoading from './SearchLoading';
import SearchEmpty from './SearchEmpty';
import ResultsContainer from './ResultsContainer';
import TestMode from '../../components/TestMode/TestMode';

interface FilterMappingProps{
  [key:string]: any,
}

const emailPlatforms = ['gmail', 'outlook'];
const messagePlatforms = ['slack', 'teams'];

const sortMap:FilterMappingProps = {
  'Relevance': 'rank',
  'Date': 'date',
}
export const filterNameMap:FilterMappingProps = {
  Gmail: 'gmail',
  Slack: 'slack',
  'Microsoft Teams': 'teams',
  'Google Drive': 'gdrive',
  Dropbox: 'dropbox',
  JIRA: 'jira',
  Outlook: 'outlook',
  Office365: 'office-365',
  'Show all': 'all',
}
export const filterMapping:FilterMappingProps = {
  Gmail: (item:any) => {
    return item.pl === 'gmail';
  },
  Slack: (item:any) => {
    return item.pl === 'slack'
  },
  'Microsoft Teams': (item:any) => {
    return item.pl === 'teams';
  },
  'Google Drive': (item: any) => {
    return item.pl === 'gdrive';
  },
  Dropbox: (item: any) => {
    return item.pl === 'dropbox'
  },
  JIRA: (item: any) => {
    return item.pl === 'jira';
  },
  Outlook: (item: any) => {
    return item.pl === 'outlook'
  },
  Office365: (item: any) => {
    return item.pl === 'office-365'
  },
  Email: (item: any) => {
    return item.filter === 'email' ||
      emailPlatforms.indexOf(item.pl) > -1
  },
  Message: (item: any) => {
    return item.filter === 'message' ||
      messagePlatforms.indexOf(item.pl) > -1
  },
  Link: (item: any) => {
    return item.filter === 'link' ||
      (item.match_links && item.match_links.length)
  },
  File: (item: any) => {
    if (item.filter === 'file') {
      return true;
    }
    if (item.pl === 'slack') {
      const matched = item.messages[item.match_pos]
      return matched && matched.att && matched.att.length;
    }

    return !!item.filename ||
      item.attachments?.length ||
      item.match_attachments?.length ||
      (item.message && item.message.attachments)
  },
  'Show all': (item: any) => { return item }
}

const SearchResults = () => {
  const [itemDetails, setItemDetails] = useState<any>(null);
  const [parseDataError, setParseDataError] = useState<any>(null);
  const bookmarks = useObservable(BookmarksStore.byId$, {});
  const searchResult = useObservable(SearchStore.results$, {});
  const searchLoading = useObservable(SearchStore.loading$, false);
  const pageLoading = useObservable(SearchStore.pageLoading$, false);
  const searchError = useObservable(SearchStore.error$, null);

  useEffect(() => {
    // remove bookmarks until further notice
    // BookmarksStore.getBookmarks();
    return () => {
      // cancel in flight page request if any
      SearchStore.cancelPageRequest();
    }
  },[]);

  const bookmarkItem = async (item:any, isMarked: boolean) => {
    if (isMarked) {
      await BookmarksStore.deleteBookmark(item);
      return;
    }
    await BookmarksStore.addBookmark(item);
  }

  const onErrorDismiss = () => {
    setParseDataError(null);
    SearchStore.clearError();
  }

  const showItemDetails = (item:any) => {
    setItemDetails(item);
  }

  const onCloseDetails = () => {
    setItemDetails(null);
  }

  const onChangeSort = (sort:any) => {
    SearchStore.setSort(sortMap[sort]);
  }

  const onFilterResults = (filter:any) => {
    SearchStore.setFilter(filterNameMap[filter.name]);
  }

  useEffect(() => {
    if (searchError && searchError.suggestions) {
      return;
    }
    setParseDataError(searchError);
  }, [searchError]);

  useEffect(() => {
    if (searchLoading) {
      setItemDetails(null);
    }
  }, [searchLoading]);


  return (
    <Styled.Container id="searchResults">
      <Styled.Container>
        <Navigation />
      </Styled.Container>
      <Container fluid="xl">
        <Row className="justify-content-between align-items-center FilterSort">
          <Col>
            <Filters isSearch onFilter={onFilterResults} />
            <div className="d-flex justify-content-end Sort">
              <Dropdown setSortName={onChangeSort}  />
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <SearchEmpty canShow={!searchLoading && !pageLoading} />
            <SearchLoading isLoading={searchLoading} />
            <ErrorBoundary onDismiss={onErrorDismiss}>
              <ResultsContainer showDetails={showItemDetails} />
            </ErrorBoundary>
          </Col>
        </Row>
      </Container>
      <ErrorModal error={parseDataError} onDismiss={onErrorDismiss} />
      <ResultDetails
        item={itemDetails}
        isOpen={!!itemDetails}
        queryTs={searchResult && searchResult.query_ts}
        isMarked={!!bookmarks[itemDetails?.result_id]}
        onBookmark={bookmarkItem}
        onClose={onCloseDetails}
      />
      <TestMode />
    </Styled.Container>
  );
}

export default SearchResults;